import type { LucideIcon } from 'lucide-react';
import { Suspense } from 'react';
import { Link, NavLink, Outlet } from 'react-router';
import { useCurrentUser } from '../../features/auth/session-context';
import { cn } from '../../lib/cn';
import { Loading } from '../ui/Status';
import { Logo } from './Logo';
import { ThemeButton } from './ThemeSheet';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Shown in the phone tab bar only (the wide layout has the account button instead). */
  phoneOnly?: boolean;
}

/**
 * The frame around every screen but the reader: a top bar with the sections on wide screens,
 * and on phones a slim top bar plus a tab bar within reach of the thumb.
 */
export function AppShell({ nav }: { nav: NavItem[] }) {
  const user = useCurrentUser();
  const initial = user.displayName.trim().charAt(0).toUpperCase() || '?';

  return (
    <div className="min-h-svh">
      <header className="safe-top sticky top-0 z-30 bg-ground">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-2 px-4 sm:px-6">
          <Logo className="mr-4" />
          <nav aria-label="Main" className="hidden flex-1 items-center gap-1 lg:flex">
            {nav
              .filter((item) => !item.phoneOnly)
              .map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    cn(
                      'flex h-10 items-center gap-2 rounded-xl px-3.5 font-medium transition-colors [&_svg]:size-4',
                      isActive ? 'bg-surface text-ink shadow-sm' : 'text-muted hover:text-ink',
                    )
                  }
                >
                  <item.icon aria-hidden />
                  {item.label}
                </NavLink>
              ))}
          </nav>
          <div className="ml-auto flex items-center gap-1">
            <ThemeButton />
            <Link
              to="/account"
              aria-label={`Account, ${user.displayName}`}
              className="hidden h-10 items-center gap-2.5 rounded-xl pr-3 pl-1 transition-colors hover:bg-raised lg:flex"
            >
              <span className="flex size-8 items-center justify-center rounded-lg bg-accent-soft font-semibold text-accent">
                {initial}
              </span>
              <span className="max-w-40 truncate font-medium">{user.displayName}</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="min-w-0 pb-[calc(5.5rem+env(safe-area-inset-bottom))] lg:pb-12">
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </main>

      <nav
        aria-label="Main"
        className="safe-bottom fixed inset-x-0 bottom-0 z-30 bg-surface shadow-[0_-1px_0_var(--raised)] lg:hidden"
      >
        <ul className="mx-auto grid max-w-lg grid-cols-4 px-2 pt-1.5 pb-1.5">
          {nav.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex flex-col items-center gap-0.5 py-1 text-[0.72rem] font-medium transition-colors',
                    isActive ? 'text-ink' : 'text-muted',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={cn(
                        'flex h-8 w-14 items-center justify-center rounded-full transition-colors [&_svg]:size-5',
                        isActive && 'bg-accent-soft text-accent',
                      )}
                    >
                      <item.icon aria-hidden />
                    </span>
                    {item.label}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
